import { useState, useEffect } from "react";
import {
  AppBar, Toolbar, Typography, Box, Avatar, TextField, InputAdornment, IconButton, Drawer, List, ListItem, ListItemIcon, ListItemButton, ListItemText, Grid, Button, AvatarGroup, ListItemAvatar
} from '@mui/material';
import { api, authenticatedFetch } from './utils/api';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import Login from "./components/Login";
import Register from "./components/Register";
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isSameDay } from 'date-fns';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpacityIcon from '@mui/icons-material/Opacity';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FlagIcon from '@mui/icons-material/Flag';
import MoodIcon from '@mui/icons-material/Mood';
import BarChartIcon from '@mui/icons-material/BarChart';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import QuickCheckinForm from './components/QuickCheckinForm';
import ProgressDashboard from './components/ProgressDashboard';
import GoalSystem from './components/GoalSystem';
import { BrowserRouter as Router, Routes, Route, useNavigate, Outlet } from 'react-router-dom';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EnhancedAIInsights from './components/EnhancedAIInsights';
import DailyCheckinModal from './components/DailyCheckinModal';
import TodayCheckinStatus from './components/TodayCheckinStatus';
import PeopleIcon from '@mui/icons-material/People';
import { ThemeContextProvider, useTheme } from './contexts/ThemeContext';
import ThemeToggle from './components/ThemeToggle';

const drawerWidth = 90;



// const sidebarIcons = [
//   { icon: <HomeIcon fontSize="large" />, label: 'Home', section: 'header-section' },
//   { icon: <CalendarMonthIcon fontSize="large" />, label: 'Calendar', section: 'progress-section' },
//   { icon: <FavoriteIcon fontSize="large" />, label: 'Wellness', section: 'focus-section' },
//   { icon: <ShowChartIcon fontSize="large" />, label: 'Progress', section: 'goal-section' },
//   { icon: <SettingsIcon fontSize="large" />, label: 'Settings', section: 'team-section' },
//   { icon: <LogoutIcon fontSize="large" />, label: 'Logout', section: 'logout' },
// ];

function MainLayout({ user, handleLogout, sidebarIcons, drawerWidth, children }) {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  // Sidebar navigation handler
  const handleSidebarClick = (section, route) => {
    if (section === 'logout') {
      handleLogout();
      return;
    }
    if (route) {
      navigate(route);
      return;
    }
    const el = document.getElementById(section);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      background: darkMode 
        ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' 
        : '#ffffff'
    }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: 'grey.100',
            borderRight: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 3,
            pb: 3,
          },
        }}
      >
        <List sx={{ flexGrow: 1 }}>
          {sidebarIcons.map((item, idx) => (
            <ListItem key={item.label} disablePadding sx={{ justifyContent: 'center', mb: 1 }}>
              <ListItemButton 
                sx={{ 
                  borderRadius: 2, 
                  minHeight: 56, 
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  }
                }}
                onClick={() => handleSidebarClick(item.section, item.route)}
              >
                <ListItemIcon sx={{ 
                  minWidth: 0, 
                  color: darkMode ? 'grey.400' : 'grey.600', 
                  justifyContent: 'center' 
                }}>
                  {item.icon}
                </ListItemIcon>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      {/* Main Content */}
      <Box sx={{ flexGrow: 1, ml: `${drawerWidth}px`, p: 0 }}>
        {/* Header */}
        <AppBar id="header-section" position="static" color="inherit" elevation={0} sx={{ 
          backgroundColor: darkMode ? '#0f172a' : '#ffffff',
          borderBottom: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
          px: 4, 
          py: 2 
        }}>
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {user?.username ? `Welcome, ${user.username}!` : 'Welcome, Friend!'}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Your wellness dashboard
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <TextField
                variant="outlined"
                placeholder="Find your goals"
                size="small"
                sx={{ 
                  minWidth: 260,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                    border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.9)',
                      border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}`,
                    },
                    '&.Mui-focused': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.95)',
                      border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(99, 102, 241, 0.5)'}`,
                    }
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <ThemeToggle />
              <Avatar alt={user?.username || 'User'} src={user?.avatarUrl || ''} sx={{ width: 48, height: 48 }} />
            </Box>
          </Toolbar>
        </AppBar>
        <Box sx={{ 
          p: 4,
          backgroundColor: darkMode ? '#0f172a' : '#ffffff',
          minHeight: 'calc(100vh - 80px)'
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

function FocusAreasCard({ focusAreas }) {
  return (
    <Box 
      id="focus-section" 
      sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3, 
        p: 3, 
        height: 'fit-content',
        boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        color: 'white'
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
        Focus Areas
      </Typography>
      <List disablePadding>
        {focusAreas.map((item, idx) => (
          <ListItem 
            key={item.label} 
            sx={{ 
              px: 0, 
              py: 2,
              mb: idx < focusAreas.length - 1 ? 1 : 0,
              borderBottom: idx < focusAreas.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Box sx={{ mr: 2, color: 'rgba(255,255,255,0.9)' }}>
                {item.icon}
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 500, 
                  fontSize: '0.9rem',
                  color: 'white',
                  flex: 1
                }}
              >
                {item.label}
              </Typography>
            </Box>
            <Box
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)', 
                px: 2, 
                py: 0.5, 
                borderRadius: 2,
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'white',
                minWidth: 'fit-content'
              }}
            >
              Today
            </Box>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

function GoalOverviewCard({ goalOverview, navigate, AddIcon }) {
  return (
    <Box 
      id="goal-section" 
      sx={{ 
        background: 'linear-gradient(135deg, #06d6a0 0%, #059669 100%)',
        borderRadius: 3, 
        p: 3, 
        height: 'fit-content',
        boxShadow: '0 10px 25px rgba(6, 214, 160, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        color: 'white'
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
        Goal Overview
      </Typography>
      <List disablePadding sx={{ mb: 3 }}>
        {goalOverview.map((item, idx) => (
          <ListItem 
            key={item.label} 
            sx={{ 
              px: 0, 
              py: 1.5,
              mb: idx < goalOverview.length - 1 ? 1 : 0,
              borderBottom: idx < goalOverview.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, mr: 2 }}>
              <Box sx={{ mr: 2, color: 'rgba(255,255,255,0.9)', minWidth: 24 }}>
                {item.icon}
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500, 
                  fontSize: '0.85rem',
                  color: 'white',
                  flex: 1
                }}
              >
                {item.label}
              </Typography>
            </Box>
            {item.avatars.length > 0 && (
              <AvatarGroup 
                max={2} 
                sx={{ 
                  '& .MuiAvatar-root': { 
                    width: 24, 
                    height: 24, 
                    fontSize: 12,
                    border: '2px solid rgba(255,255,255,0.3)'
                  },
                  flexShrink: 0
                }}
              >
                {item.avatars.map((src, i) => (
                  <Avatar key={i} src={src} />
                ))}
              </AvatarGroup>
            )}
          </ListItem>
        ))}
      </List>
      <Box sx={{ textAlign: 'center' }}>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          sx={{ 
            fontWeight: 600, 
            borderRadius: 3,
            px: 4,
            py: 1.5,
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.3)',
            },
            backdropFilter: 'blur(10px)'
          }} 
          onClick={() => navigate('/checkin')}
        >
          Log Now
        </Button>
      </Box>
    </Box>
  );
}

function DashboardMain({ user, handleLogout, sidebarIcons, drawerWidth, focusAreas, goalOverview, recentUpdates, teamSupport, CheckinDay, AdapterDateFns, LocalizationProvider, AddIcon, ArrowForwardIosIcon }) {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [checkins, setCheckins] = useState([]);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch checkins for calendar
  useEffect(() => {
    const fetchCheckins = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(api.checkins, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setCheckins(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching checkins:', error);
      }
    };
    fetchCheckins();
  }, []);

  // Handle day click
  const handleDayClick = (day) => {
    setSelectedDate(day);
    setModalOpen(true);
  };

  // Progress Ring Component
  const ProgressRing = ({ progress, size = 120, strokeWidth = 8, color = '#6366f1' }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDasharray = `${circumference} ${circumference}`;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <svg height={size} width={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            stroke="rgba(255,255,255,0.2)"
            fill="transparent"
            strokeWidth={strokeWidth}
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          <circle
            stroke={color}
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            r={radius}
            cx={size / 2}
            cy={size / 2}
            style={{
              transition: 'stroke-dashoffset 1.5s ease-in-out',
            }}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'white' }}>
            {Math.round(progress)}%
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
            Complete
          </Typography>
        </Box>
      </Box>
    );
  };

  // Interactive CheckinDay component
  function InteractiveCheckinDay(props) {
    const { day, outsideCurrentMonth, ...other } = props;
    
    const dayCheckins = checkins.filter(checkin => 
      isSameDay(new Date(checkin.timestamp), day)
    );
    
    const hasCheckin = dayCheckins.length > 0;

    return (
      <Box
        onClick={() => handleDayClick(day)}
        sx={{
          position: 'relative',
          cursor: hasCheckin ? 'pointer' : 'default',
          '&:hover': hasCheckin ? { bgcolor: 'action.hover' } : {},
          borderRadius: 1,
          transition: 'background-color 0.2s'
        }}
      >
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          sx={{
            ...(hasCheckin && {
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
            }),
          }}
        />
        {hasCheckin && (
          <Box sx={{
            position: 'absolute', top: 2, right: 2, width: 8, height: 8,
            borderRadius: '50%', bgcolor: 'success.main', border: '1px solid white',
          }} />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      pt: 2,
      pb: 4,
      position: 'relative'
    }}>
      {/* Floating background elements with dark mode support */}
      <Box sx={{ 
        position: 'absolute', 
        top: '20%', 
        left: '10%', 
        width: 200, 
        height: 200, 
        background: darkMode 
          ? 'rgba(129, 140, 248, 0.1)' 
          : 'rgba(99, 102, 241, 0.1)', 
        borderRadius: '50%',
        filter: 'blur(40px)',
        animation: 'float 8s ease-in-out infinite',
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px) scale(1)' },
          '50%': { transform: 'translateY(-30px) scale(1.1)' }
        }
      }} />
      
      <Box sx={{ 
        position: 'absolute', 
        top: '60%', 
        right: '15%', 
        width: 150, 
        height: 150, 
        background: darkMode 
          ? 'rgba(52, 211, 153, 0.1)' 
          : 'rgba(6, 214, 160, 0.1)', 
        borderRadius: '50%',
        filter: 'blur(30px)',
        animation: 'float 6s ease-in-out infinite reverse',
      }} />

      <Grid container spacing={4}>
        {/* Today's Check-in Status */}
        <Grid item xs={12}>
          <TodayCheckinStatus />
        </Grid>

        {/* Enhanced Today's Progress Card */}
        <Grid item xs={12} md={6} lg={4}>
          <Box 
            id="progress-section" 
            sx={{ 
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              borderRadius: 4, 
              p: 4, 
              height: 'fit-content',
              boxShadow: '0 25px 50px rgba(30, 41, 59, 0.4)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 35px 70px rgba(30, 41, 59, 0.6)',
              }
            }}
          >
            {/* Animated background pattern */}
            <Box sx={{ 
              position: 'absolute', 
              top: -100, 
              right: -100, 
              width: 200, 
              height: 200, 
              background: 'radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%)',
              animation: 'pulse 4s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 0.3, transform: 'scale(1)' },
                '50%': { opacity: 0.6, transform: 'scale(1.1)' }
              }
            }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, position: 'relative', zIndex: 1 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'white', mb: 1 }}>
                  📊 Today's Progress
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  You're crushing it today!
                </Typography>
              </Box>
              <Box sx={{ 
                bgcolor: 'rgba(99, 102, 241, 0.2)', 
                color: 'white', 
                px: 3, 
                py: 1, 
                borderRadius: 3,
                fontWeight: 700,
                border: '1px solid rgba(99, 102, 241, 0.3)'
              }}>
                {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, position: 'relative', zIndex: 1 }}>
              <ProgressRing progress={78} size={160} color="#6366f1" />
            </Box>
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                  slots={{ day: InteractiveCheckinDay }}
                  disableFuture
                  sx={{ 
                    color: 'white',
                    '& .MuiPickersCalendarHeader-root': {
                      color: 'white',
                      paddingLeft: 1,
                      paddingRight: 1
                    },
                    '& .MuiPickersCalendarHeader-label': {
                      color: 'white',
                      fontWeight: 700
                    },
                    '& .MuiIconButton-root': {
                      color: 'white'
                    },
                    '& .MuiDayCalendar-weekDayLabel': {
                      fontSize: '0.875rem',
                      color: 'rgba(255,255,255,0.8)',
                      fontWeight: 600
                    },
                    '& .MuiPickersDay-root': {
                      fontSize: '0.875rem',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(99, 102, 241, 0.3)'
                      }
                    },
                    '& .Mui-selected': {
                      bgcolor: '#6366f1 !important',
                      color: 'white'
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        </Grid>

      {/* Focus Areas Card */}
      <Grid item xs={12} md={6} lg={4}>
        <FocusAreasCard focusAreas={focusAreas} />
      </Grid>

      {/* Goal Overview Card */}
      <Grid item xs={12} md={6} lg={4}>
        <GoalOverviewCard goalOverview={goalOverview} navigate={navigate} AddIcon={AddIcon} />
      </Grid>

      {/* Recent Updates Card */}
      <Grid item xs={12} md={6} lg={6}>
        <Box 
          id="recent-section" 
          sx={{ 
            bgcolor: 'background.paper', 
            borderRadius: 3, 
            p: 3, 
            height: 'fit-content',
            boxShadow: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Updates</Typography>
          <List disablePadding>
            {recentUpdates.map((item, idx) => (
              <ListItem 
                key={idx} 
                sx={{ 
                  px: 0, 
                  py: 1.5, 
                  mb: idx < recentUpdates.length - 1 ? 1 : 0, 
                  bgcolor: 'grey.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.100'
                }}
                secondaryAction={
                  <ArrowForwardIosIcon 
                    fontSize="small" 
                    color="action" 
                    sx={{ mr: 1 }}
                  />
                }
              >
                <ListItemAvatar sx={{ ml: 1 }}>
                  <Avatar src={item.avatar} sx={{ width: 36, height: 36 }} />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {item.name}{' '}
                      <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                        {item.action}
                      </Box>
                    </Typography>
                  }
                  secondary={
                    <Typography 
                      variant="body2" 
                      color="primary" 
                      fontWeight={500}
                      sx={{ mt: 0.5 }}
                    >
                      {item.detail}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Grid>

      {/* Team Support Card */}
      <Grid item xs={12} md={6} lg={6}>
        <Box 
          id="team-section" 
          sx={{ 
            bgcolor: 'background.paper', 
            borderRadius: 3, 
            p: 3, 
            height: 'fit-content',
            boxShadow: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Team Support</Typography>
          <Grid container spacing={2}>
            {teamSupport.map((member, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    bgcolor: 'grey.50', 
                    borderRadius: 2, 
                    p: 2,
                    border: '1px solid',
                    borderColor: 'grey.100',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      cursor: 'pointer'
                    }
                  }}
                >
                  <Avatar src={member.avatar} sx={{ width: 40, height: 40 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={600}
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {member.name}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block'
                      }}
                    >
                      {member.role}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Grid>

      {/* Quick Actions / Tags Card */}
      <Grid item xs={12}>
        <Box 
          sx={{ 
            bgcolor: 'background.paper', 
            borderRadius: 3, 
            p: 3, 
            boxShadow: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Quick Actions</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<AddCircleIcon />}
                onClick={() => navigate('/checkin')}
                sx={{ py: 1.5, borderRadius: 2 }}
              >
                Check-in
              </Button>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ShowChartIcon />}
                onClick={() => navigate('/progress')}
                sx={{ py: 1.5, borderRadius: 2 }}
              >
                Progress
              </Button>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<SmartToyIcon />}
                onClick={() => navigate('/ai')}
                sx={{ py: 1.5, borderRadius: 2 }}
              >
                AI Chat
              </Button>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<PeopleIcon />}
                onClick={() => navigate('/friends')}
                sx={{ py: 1.5, borderRadius: 2 }}
              >
                Friends
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
    
    {/* Daily Check-in Modal */}
    <DailyCheckinModal 
      open={modalOpen} 
      onClose={() => setModalOpen(false)} 
      selectedDate={selectedDate} 
      checkins={checkins} 
    />
    </Box>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [, setToken] = useState(null);
  const [showRegister, setShowRegister] = useState(false);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
  }, []);

  const handleLogin = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
  };

  const handleRegister = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);
  };

  const mockCheckinDates = [
    new Date(),
    new Date(new Date().setDate(new Date().getDate() - 1)),
    new Date(new Date().setDate(new Date().getDate() - 3)),
    new Date(new Date().setDate(new Date().getDate() - 5)),
  ];

  // Custom day renderer to show a dot for check-in days
  function CheckinDay(props) {
    const { day, outsideCurrentMonth, ...other } = props;
    const hasCheckin = mockCheckinDates.some(d => isSameDay(d, day));
    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        sx={{
          ...(hasCheckin && {
            '& .MuiPickersDay-dayWithMargin': {
              borderBottom: '4px solid #1976d2',
            },
            borderBottom: '4px solid #1976d2',
          }),
        }}
      />
    );
  }

  const focusAreas = [
    { icon: <CheckCircleOutlineIcon color="primary" />, label: 'Log your daily check-in' },
    { icon: <OpacityIcon color="primary" />, label: 'Hydration tracking' },
    { icon: <RestaurantIcon color="primary" />, label: 'Nutrition goals' },
  ];

  const goalOverview = [
    { icon: <TrendingUpIcon color="primary" />, label: 'Weight goal progress', avatars: ["/avatars/1.png", "/avatars/2.png"] },
    { icon: <AssignmentTurnedInIcon color="primary" />, label: 'Daily check-ins', avatars: ["/avatars/3.png"] },
    { icon: <FlagIcon color="primary" />, label: 'Goal setting', avatars: [] },
    { icon: <MoodIcon color="primary" />, label: 'Mood and energy logs', avatars: ["/avatars/1.png", "/avatars/2.png", "/avatars/3.png"] },
    { icon: <BarChartIcon color="primary" />, label: 'Progress visualization', avatars: [] },
  ];

  const recentUpdates = [
    {
      avatar: '/avatars/2.png',
      name: 'Alex T.',
      action: 'logged mood today',
      detail: 'Check my hydration stats...'
    },
    {
      avatar: '/avatars/3.png',
      name: 'Jamie L.',
      action: 'set new goals',
      detail: 'Let\'s review my fitness plan.'
    },
  ];

  const teamSupport = [
    { avatar: '/avatars/3.png', name: 'Jamie L.', role: 'Fitness Coach' },
    { avatar: '/avatars/2.png', name: 'Alex T.', role: 'Nutrition Expert' },
    { avatar: '/avatars/4.png', name: 'Taylor R.', role: 'Wellness Advisor' },
    { avatar: '/avatars/5.png', name: 'Jordan K.', role: 'Fitness Trainer' },
  ];

  // Update sidebarIcons to include routes
  const sidebarIcons = [
    { icon: <HomeIcon fontSize="large" />, label: 'Home', section: 'header-section', route: '/' },
    { icon: <CalendarMonthIcon fontSize="large" />, label: 'Calendar', section: 'progress-section', route: '/progress' },
    { icon: <AddCircleIcon fontSize="large" />, label: 'Check-In', section: 'checkin-section', route: '/checkin' },
    { icon: <FlagIcon fontSize="large" />, label: 'Goals', section: 'goals-section', route: '/goals' },
    { icon: <SmartToyIcon fontSize="large" />, label: 'AI', section: 'ai-section', route: '/ai' },
    { icon: <PeopleIcon fontSize="large" />, label: 'Friends', section: 'friends-section', route: '/friends' },
    { icon: <LogoutIcon fontSize="large" />, label: 'Logout', section: 'logout', route: null },
  ];

  // Wrap the entire app in ThemeContextProvider
  return (
    <ThemeContextProvider>
      <AppContent 
        user={user}
        showRegister={showRegister}
        setShowRegister={setShowRegister}
        handleLogin={handleLogin}
        handleRegister={handleRegister}
        handleLogout={handleLogout}
        sidebarIcons={sidebarIcons}
        focusAreas={focusAreas}
        goalOverview={goalOverview}
        recentUpdates={recentUpdates}
        teamSupport={teamSupport}
        CheckinDay={CheckinDay}
        AdapterDateFns={AdapterDateFns}
        LocalizationProvider={LocalizationProvider}
        AddIcon={AddIcon}
        ArrowForwardIosIcon={ArrowForwardIosIcon}
      />
    </ThemeContextProvider>
  );
}

// Separate component to access theme context
function AppContent({ user, showRegister, setShowRegister, handleLogin, handleRegister, handleLogout, sidebarIcons, focusAreas, goalOverview, recentUpdates, teamSupport, CheckinDay, AdapterDateFns, LocalizationProvider, AddIcon, ArrowForwardIosIcon }) {
  const { darkMode } = useTheme();

  if (!user) {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        mt: 4,
        minHeight: '100vh',
        background: darkMode 
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Box sx={{ pt: 8 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              color: darkMode ? 'primary.main' : 'white',
              textShadow: darkMode ? 'none' : '0 2px 4px rgba(0,0,0,0.3)',
              fontWeight: 800
            }} 
            gutterBottom
          >
            FitCoach AI
          </Typography>
          <Box sx={{ 
            position: 'absolute', 
            top: 20, 
            right: 20 
          }}>
            <ThemeToggle />
          </Box>
        </Box>
        {showRegister ? (
          <div>
            <Register onRegister={handleRegister} onSwitchToLogin={() => setShowRegister(false)} />
            <Typography align="center" sx={{ 
              mt: 2, 
              color: darkMode ? 'text.primary' : 'white',
              textShadow: darkMode ? 'none' : '0 1px 2px rgba(0,0,0,0.3)'
            }}>
              Already have an account?{' '}
              <Box component="span" sx={{ 
                color: darkMode ? 'primary.main' : '#fbbf24', 
                cursor: 'pointer', 
                textDecoration: 'underline',
                fontWeight: 600
              }} onClick={() => setShowRegister(false)}>
                Login here
              </Box>
            </Typography>
          </div>
        ) : (
          <div>
            <Login onLogin={handleLogin} onSwitchToRegister={() => setShowRegister(true)} />
            <Typography align="center" sx={{ 
              mt: 2, 
              color: darkMode ? 'text.primary' : 'white',
              textShadow: darkMode ? 'none' : '0 1px 2px rgba(0,0,0,0.3)'
            }}>
              Don't have an account?{' '}
              <Box component="span" sx={{ 
                color: darkMode ? 'primary.main' : '#fbbf24', 
                cursor: 'pointer', 
                textDecoration: 'underline',
                fontWeight: 600
              }} onClick={() => setShowRegister(true)}>
                Register here
              </Box>
            </Typography>
          </div>
        )}
      </Box>
    );
  }

  return (
    <Router>
      <Routes>
        <Route element={
          <MainLayout
            user={user}
            handleLogout={handleLogout}
            sidebarIcons={sidebarIcons}
            drawerWidth={drawerWidth}
          />
        }>
          <Route path="/" element={
            <DashboardMain
              user={user}
              handleLogout={handleLogout}
              sidebarIcons={sidebarIcons}
              drawerWidth={drawerWidth}
              focusAreas={focusAreas}
              goalOverview={goalOverview}
              recentUpdates={recentUpdates}
              teamSupport={teamSupport}
              CheckinDay={CheckinDay}
              AdapterDateFns={AdapterDateFns}
              LocalizationProvider={LocalizationProvider}
              AddIcon={AddIcon}
              ArrowForwardIosIcon={ArrowForwardIosIcon}
            />
          } />
          <Route path="/checkin" element={<QuickCheckinForm />} />
          <Route path="/progress" element={<ProgressDashboard />} />
          <Route path="/goals" element={<GoalSystem />} />
          <Route path="/ai" element={<EnhancedAIInsights />} />
          <Route path="/friends" element={<FriendsPage />} />
        </Route>
      </Routes>
    </Router>
  );
}

const FriendsPage = () => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch current friends on mount
  useEffect(() => {
    fetchFriends();
  }, []);

  const fetchFriends = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(api.friends, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setFriends(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load friends');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResults([]);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(api.usersSearch(search), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFriend = async (userId) => {
    setAdding(userId);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(api.friendsAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ friendId: userId })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess('Friend added!');
        fetchFriends();
      } else {
        setError(data.error || 'Failed to add friend');
      }
    } catch (err) {
      setError('Failed to add friend');
    } finally {
      setAdding('');
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: '40px auto', padding: 32, background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px #0001' }}>
      <h2><PeopleIcon style={{ verticalAlign: 'middle', marginRight: 8 }} /> Friends</h2>
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <TextField
          label="Search users"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          fullWidth
        />
        <Button type="submit" variant="contained" disabled={loading || !search.trim()}>Search</Button>
      </form>
      {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
      {success && <div style={{ color: 'green', marginBottom: 12 }}>{success}</div>}
      {results.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h4>Search Results</h4>
          {results.map(user => (
            <div key={user._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 8, borderBottom: '1px solid #eee' }}>
              <span>{user.username} <span style={{ color: '#888', fontSize: 13 }}>({user.email})</span></span>
              <Button
                variant="outlined"
                size="small"
                disabled={adding === user._id || friends.some(f => f._id === user._id)}
                onClick={() => handleAddFriend(user._id)}
              >
                {friends.some(f => f._id === user._id) ? 'Friend' : adding === user._id ? 'Adding...' : 'Add Friend'}
              </Button>
            </div>
          ))}
        </div>
      )}
      <h4>Your Friends</h4>
      {loading ? <div>Loading...</div> : (
        <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
          {friends.length === 0 && <li style={{ color: '#888' }}>No friends yet.</li>}
          {friends.map(friend => (
            <li key={friend._id} style={{ padding: 8, borderBottom: '1px solid #eee' }}>
              {friend.username} <span style={{ color: '#888', fontSize: 13 }}>({friend.email})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default App;